export type StarlinkNodeKind = "platform" | "app" | "subapp" | "admin";
export type StarlinkNodeStatus = "active" | "maintenance" | "planned";
export type StarlinkTheme = "light" | "dark" | "immersive";
export type StarlinkIcon =
  | "grid"
  | "cube"
  | "memory"
  | "video"
  | "course"
  | "person"
  | "world"
  | "leaf"
  | "insect"
  | "bird"
  | "history"
  | "sail"
  | "myth"
  | "university"
  | "station"
  | "inquiry"
  | "experiment"
  | "results";

export type StarlinkAppNode = {
  id: string;
  parentId?: string;
  name: string;
  shortName?: string;
  description?: string;
  icon: StarlinkIcon;
  launchPath: string;
  destination?: "starlink" | "eco";
  kind: StarlinkNodeKind;
  status: StarlinkNodeStatus;
  permission?: string;
  theme: StarlinkTheme;
};

export const STARLINK_ROOT_ID = "ai-starlink";

/**
 * Product navigation is deliberately independent from `starlinkAppRegistry`.
 * The registry above/below represents deployable application ancestry used by
 * existing consumers, while these catalogs represent the Console's two-axis
 * learning architecture.
 */
export type StarLearningThemeId = "ai-eco" | "ai-sail" | "ai-3d";
export type StarLearningEntryId = "world-view" | "course" | "inquiry" | "observation" | "experiment" | "results";
export type StarExtendedAppId = "ai-video" | "campus-memory" | "story-maker" | "digital-human";
export type StarPlatformSurface = "home" | "catalog" | "themes" | "extended-apps";
export type StarProductNavigationStatus = "active" | "partial" | "planned";

export type StarLearningThemeItem = {
  id: StarLearningThemeId;
  name: string;
  shortName: string;
  icon: StarlinkIcon;
  accent: string;
  launchPath: string;
};

export type StarLearningEntryItem = {
  id: StarLearningEntryId;
  name: string;
  icon: StarlinkIcon;
  launchPath: string;
  status: StarProductNavigationStatus;
  permission?: string;
};

export type StarExtendedAppItem = {
  id: StarExtendedAppId;
  name: string;
  icon: StarlinkIcon;
  launchPath: string;
  status: StarProductNavigationStatus;
  permission?: string;
};

export type StarPrimaryAppId =
  | "ai-3d"
  | "campus-memory"
  | "ai-eco"
  | "ai-video"
  | "course-center"
  | "inquiry-learning"
  | "digital-human"
  | "world-view";

export type StarPrimaryAppItem = {
  id: StarPrimaryAppId;
  name: string;
  icon: StarlinkIcon;
  launchPath: string;
  status: StarProductNavigationStatus;
  permission?: string;
};

export const STAR_PLATFORM_PATHS = {
  home: "/",
  catalog: "/catalog",
  themes: "/themes",
  extendedApps: "/apps/extended"
} as const;

export const starLearningThemes = [
  {
    id: "ai-eco",
    name: "AI 生态",
    shortName: "生态",
    icon: "leaf",
    accent: "#6EE7B7",
    launchPath: "/themes/ai-eco"
  },
  {
    id: "ai-sail",
    name: "AI 航海",
    shortName: "航海",
    icon: "sail",
    accent: "#60A5FA",
    launchPath: "/themes/ai-sail"
  },
  {
    id: "ai-3d",
    name: "AI 3D",
    shortName: "3D",
    icon: "cube",
    accent: "#FACC15",
    launchPath: "/themes/ai-3d"
  }
] as const satisfies readonly StarLearningThemeItem[];

export const starLearningEntries = [
  {
    id: "world-view",
    name: "AI 看世界",
    icon: "world",
    launchPath: "/entries/world-view",
    status: "active",
    permission: "world-view"
  },
  {
    id: "course",
    name: "格物课程",
    icon: "course",
    launchPath: "/entries/course",
    status: "active",
    permission: "course-center"
  },
  {
    id: "inquiry",
    name: "格物探究",
    icon: "inquiry",
    launchPath: "/entries/inquiry",
    status: "active",
    permission: "inquiry-learning"
  },
  {
    id: "observation",
    name: "格物观测",
    icon: "station",
    launchPath: "/entries/observation",
    status: "partial",
    permission: "ai-eco"
  },
  {
    id: "experiment",
    name: "格物实验",
    icon: "experiment",
    launchPath: "/entries/experiment",
    status: "partial",
    permission: "ai-eco"
  },
  {
    id: "results",
    name: "创意展厅",
    icon: "results",
    launchPath: "/entries/results",
    status: "planned"
  }
] as const satisfies readonly StarLearningEntryItem[];

/**
 * Public first-level business applications for the Journey V2 switcher.
 * AI星链 itself is rendered as the platform home, Identity is infrastructure,
 * and Story Maker remains a specialist creation engine rather than a public
 * first-level application. This catalog is intentionally separate from the
 * deployable registry and the legacy learning-entry axis.
 */
export const starPrimaryApps = [
  {
    id: "ai-3d",
    name: "AI 3D",
    icon: "cube",
    launchPath: "/3d-print",
    status: "active",
    permission: "ai-3d"
  },
  {
    id: "campus-memory",
    name: "校园记忆",
    icon: "memory",
    launchPath: "/campus-memory",
    status: "active",
    permission: "campus-memory"
  },
  {
    id: "ai-eco",
    name: "AI 生态",
    icon: "leaf",
    launchPath: "/ai-eco",
    status: "active",
    permission: "ai-eco"
  },
  {
    id: "ai-video",
    name: "AI 影视",
    icon: "video",
    launchPath: "/ai-video",
    status: "active",
    permission: "ai-video"
  },
  {
    id: "course-center",
    name: "课程中心",
    icon: "course",
    launchPath: "/course-center",
    status: "active",
    permission: "course-center"
  },
  {
    id: "inquiry-learning",
    name: "爱科创",
    icon: "inquiry",
    launchPath: "/inquiry-learning",
    status: "active",
    permission: "inquiry-learning"
  },
  {
    id: "digital-human",
    name: "AI 数字人",
    icon: "person",
    launchPath: "/products/digital-human",
    status: "planned",
    permission: "ai-agent"
  },
  {
    id: "world-view",
    name: "AI 看世界",
    icon: "world",
    launchPath: "/world-view",
    status: "active",
    permission: "world-view"
  }
] as const satisfies readonly StarPrimaryAppItem[];

export const starExtendedApps = [
  {
    id: "ai-video",
    name: "AI 影视",
    icon: "video",
    launchPath: "/ai-video",
    status: "active",
    permission: "ai-video"
  },
  {
    id: "campus-memory",
    name: "校园记忆",
    icon: "memory",
    launchPath: "/campus-memory",
    status: "active",
    permission: "campus-memory"
  },
  {
    id: "story-maker",
    name: "AI 教育故事",
    icon: "course",
    launchPath: "/story-maker",
    status: "active",
    permission: "story-maker"
  },
  {
    id: "digital-human",
    name: "AI 数字人",
    icon: "person",
    launchPath: "/digital-human",
    status: "active",
    permission: "ai-agent"
  }
] as const satisfies readonly StarExtendedAppItem[];

export function isStarLearningThemeId(value: string | null | undefined): value is StarLearningThemeId {
  return starLearningThemes.some((theme) => theme.id === value);
}

export function isStarLearningEntryId(value: string | null | undefined): value is StarLearningEntryId {
  return starLearningEntries.some((entry) => entry.id === value);
}

export function isStarPlatformSurface(value: string | null | undefined): value is StarPlatformSurface {
  return ["home", "catalog", "themes", "extended-apps"].includes(value ?? "");
}

export function getStarLearningTheme(id: string): StarLearningThemeItem | undefined {
  return (starLearningThemes as readonly StarLearningThemeItem[]).find((theme) => theme.id === id);
}

export function getStarLearningEntry(id: string): StarLearningEntryItem | undefined {
  return (starLearningEntries as readonly StarLearningEntryItem[]).find((entry) => entry.id === id);
}

export const starlinkAppRegistry = [
  { id: STARLINK_ROOT_ID, name: "产品总览", shortName: "Star", icon: "grid", launchPath: "/", kind: "platform", status: "active", theme: "dark" },
  { id: "ai-3d", parentId: STARLINK_ROOT_ID, name: "AI 3D", icon: "cube", launchPath: "/3d-print", kind: "app", status: "active", permission: "ai-3d", theme: "dark" },
  { id: "campus-memory", parentId: STARLINK_ROOT_ID, name: "校园记忆", icon: "memory", launchPath: "/campus-memory", kind: "app", status: "active", permission: "campus-memory", theme: "light" },
  { id: "ai-video", parentId: STARLINK_ROOT_ID, name: "AI影视", icon: "video", launchPath: "/ai-video", kind: "app", status: "active", permission: "ai-video", theme: "dark" },
  { id: "course-center", parentId: STARLINK_ROOT_ID, name: "课程中心", icon: "course", launchPath: "/course-center", kind: "app", status: "active", permission: "course-center", theme: "light" },
  { id: "inquiry-learning", parentId: STARLINK_ROOT_ID, name: "格物探究", icon: "inquiry", launchPath: "/inquiry-learning", kind: "app", status: "active", permission: "inquiry-learning", theme: "light" },
  { id: "story-maker", parentId: STARLINK_ROOT_ID, name: "AI 教育故事", icon: "course", launchPath: "/story-maker", kind: "app", status: "active", permission: "story-maker", theme: "light" },
  { id: "digital-human", parentId: STARLINK_ROOT_ID, name: "AI 数字人", icon: "person", launchPath: "/digital-human", kind: "app", status: "active", permission: "ai-agent", theme: "immersive" },
  { id: "world-view", parentId: STARLINK_ROOT_ID, name: "AI看世界", description: "6 个探索主题", icon: "world", launchPath: "/world-view", kind: "app", status: "active", permission: "world-view", theme: "immersive" },
  { id: "insect-discovery", parentId: "world-view", name: "昆虫探秘", icon: "insect", launchPath: "/world-view/insect-discovery", kind: "subapp", status: "active", permission: "world-view:insect-discovery", theme: "immersive" },
  { id: "birdatlas", parentId: "world-view", name: "飞羽星图", icon: "bird", launchPath: "/world-view/feather-star-map", kind: "subapp", status: "active", permission: "world-view:feather-star-map", theme: "immersive" },
  { id: "history-map", parentId: "world-view", name: "时空人物", icon: "history", launchPath: "/world-view/history-star-map", kind: "subapp", status: "active", permission: "world-view:history-star-map", theme: "immersive" },
  { id: "sail-ai", parentId: "world-view", name: "航海探索", icon: "sail", launchPath: "/world-view/sail-ai", kind: "subapp", status: "active", permission: "world-view:sail-ai", theme: "immersive" },
  { id: "myth-map", parentId: "world-view", name: "世界神话", icon: "myth", launchPath: "/world-view/world-myth-map", kind: "subapp", status: "active", permission: "world-view:world-myth-map", theme: "immersive" },
  { id: "university-map", parentId: "world-view", name: "世界大学", icon: "university", launchPath: "/world-view/university-map", kind: "subapp", status: "active", permission: "world-view:university-map", theme: "immersive" },
  { id: "ai-eco", parentId: STARLINK_ROOT_ID, name: "AI生态", description: "格物工作台", icon: "leaf", launchPath: "/ai-eco", kind: "app", status: "active", permission: "ai-eco", theme: "dark" },
  { id: "gewu-observatory", parentId: "ai-eco", name: "格物观测站", icon: "station", launchPath: "/console", destination: "eco", kind: "subapp", status: "active", permission: "ai-eco:explore", theme: "dark" },
  { id: "gewu-inquiry", parentId: "ai-eco", name: "格物实验学习平台", icon: "inquiry", launchPath: "/experiment-platform?view=overview", destination: "eco", kind: "subapp", status: "active", permission: "ai-eco:inquiry", theme: "light" }
] as const satisfies readonly StarlinkAppNode[];

export type StarlinkAppId = (typeof starlinkAppRegistry)[number]["id"];

export function getStarlinkNode(id: string): StarlinkAppNode | undefined {
  return (starlinkAppRegistry as readonly StarlinkAppNode[]).find((node) => node.id === id);
}

export function getStarlinkChildren(parentId: string): StarlinkAppNode[] {
  return (starlinkAppRegistry as readonly StarlinkAppNode[]).filter((node) => node.parentId === parentId);
}
